// Items and units of measure — data access.
//
//   list   rpc item_page(type, term, inactive, filters, sort, after, limit) —
//          list, search, column filters and sort in one SECURITY DEFINER
//          function with an explicit tenant filter (a user's LIKE under RLS is
//          not leakproof and could not use an index). Keyset on the sort
//          column + code (sort_cursor), limit+1 → hasMore.
//          index: items_tenant_code_key (default sort), items_tenant_type_code_idx
//          (type chip), items_search_idx GIN trgm (term); other sorts are a
//          top-N over the tenant's items (master data, bounded).
//   detail one request: item + base unit + item conversions (embedded)
//   writes plain insert/update; RLS + column grants enforce permissions and
//          immutability (code); audit rows come from triggers.
// Row types are hand-written until `supabase gen types` runs in CI.

import { infiniteQueryOptions, queryOptions } from "@tanstack/react-query";
import { isDemoMode, supabase } from "@/lib/supabase";
import { demoApi } from "./demo";
import { sortParam, type ListFilter, type ListSort } from "@patronix/domain/list";

export const itemTypes = ["material", "semi_finished", "finished_good", "service"] as const;
export type ItemType = (typeof itemTypes)[number];
export type ProcurementType = "purchase" | "manufacture";
export type ItemTypeFilter = "all" | ItemType;

export const uomDimensions = ["count", "mass", "length", "area", "volume", "time"] as const;
export type UomDimension = (typeof uomDimensions)[number];

export interface Uom {
  id: string;
  code: string;
  name: string;
  dimension: UomDimension;
  decimals: number;
  is_active: boolean;
}

export interface ItemRow {
  id: string;
  code: string;
  description: string;
  item_type: ItemType;
  procurement_type: ProcurementType;
  lot_controlled: boolean;
  serial_controlled: boolean;
  is_active: boolean;
  base_uom: { code: string } | null;
}

export interface ItemConversion {
  id: string;
  factor: string; // numeric as string
  from_uom: { id: string; code: string } | null;
  to_uom: { id: string; code: string } | null;
}

export interface ItemDetail extends Omit<ItemRow, "base_uom"> {
  base_uom_id: string;
  base_uom: { id: string; code: string; name: string; decimals: number } | null;
  updated_at: string;
  conversions: ItemConversion[];
}

export interface ItemInput {
  code: string;
  description: string;
  item_type: ItemType;
  base_uom_id: string;
  procurement_type: ProcurementType;
  lot_controlled: boolean;
  serial_controlled: boolean;
  is_active: boolean;
}

export interface ItemListParams {
  type: ItemTypeFilter;
  q: string;
  inactive: boolean;
  /** Column filters and sort (item_page spec, itemListColumns). */
  filters?: ListFilter[];
  sort?: ListSort | null;
}

export interface ItemPage {
  rows: ItemRow[];
  nextCursor: string | null;
}

export const PAGE_SIZE = 50;

const detailColumns =
  "id,code,description,item_type,procurement_type,lot_controlled,serial_controlled,is_active,base_uom_id,updated_at," +
  "base_uom:uoms!items_base_uom_fkey(id,code,name,decimals)," +
  "conversions:uom_conversions!uom_conversions_item_fkey(id,factor,from_uom:uoms!uom_conversions_from_fkey(id,code),to_uom:uoms!uom_conversions_to_fkey(id,code))";

function db() {
  if (!supabase) throw new Error("Supabase is not configured");
  return supabase;
}

interface SearchRow extends Omit<ItemRow, "base_uom"> {
  base_uom_code: string;
  sort_cursor: unknown;
}

export async function listItems(p: ItemListParams, cursor: string | null, signal: AbortSignal, size = PAGE_SIZE): Promise<ItemPage> {
  if (isDemoMode) return demoApi.listItems(p, cursor);
  // One RPC for list, search, column filters and sort (item_page, keyset on
  // the sort column + code). Wildcards in the term are escaped server-side.
  const { data, error } = await db()
    .rpc("item_page", {
      p_item_type: p.type === "all" ? null : p.type,
      p_term: p.q.trim() || null,
      p_include_inactive: p.inactive,
      p_filters: p.filters ?? [],
      p_sort: sortParam(p.sort ?? null),
      p_after: cursor ? JSON.parse(cursor) : null,
      p_limit: size + 1,
    })
    .abortSignal(signal);
  if (error) throw error;
  const rows = (data as SearchRow[]).map(({ base_uom_code, sort_cursor, ...r }) => ({ row: { ...r, base_uom: { code: base_uom_code } }, sort_cursor }));
  const hasMore = rows.length > size;
  const page = hasMore ? rows.slice(0, size) : rows;
  return { rows: page.map((r) => r.row), nextCursor: hasMore ? JSON.stringify(page[page.length - 1]!.sort_cursor) : null };
}

/**
 * Items by code, for pasted lines: one request per 200 codes on the unique
 * (tenant_id, code) index; only the columns the line pickers use.
 */
export async function itemsByCodes(codes: readonly string[], signal?: AbortSignal): Promise<ItemRow[]> {
  const unique = [...new Set(codes.map((c) => c.trim().toUpperCase()).filter(Boolean))];
  if (!unique.length) return [];
  if (isDemoMode) {
    const all = await demoApi.listItems({ type: "all", q: "", inactive: false }, null);
    return all.rows.filter((r) => unique.includes(r.code.toUpperCase()));
  }
  const out: ItemRow[] = [];
  for (let i = 0; i < unique.length; i += 200) {
    let q = db()
      .from("items")
      .select("id,code,description,item_type,procurement_type,lot_controlled,serial_controlled,is_active,base_uom:uoms!items_base_uom_fkey(code)")
      .in("code", unique.slice(i, i + 200));
    if (signal) q = q.abortSignal(signal);
    const { data, error } = await q;
    if (error) throw error;
    out.push(...(data as unknown as ItemRow[]));
  }
  return out;
}

async function getItem(id: string, signal: AbortSignal): Promise<ItemDetail | null> {
  if (isDemoMode) return demoApi.getItem(id);
  const { data, error } = await db().from("items").select(detailColumns).eq("id", id).abortSignal(signal).maybeSingle();
  if (error) throw error;
  return data as unknown as ItemDetail | null;
}

async function listUoms(signal: AbortSignal): Promise<Uom[]> {
  if (isDemoMode) return demoApi.listUoms();
  const { data, error } = await db()
    .from("uoms")
    .select("id,code,name,dimension,decimals,is_active")
    .order("code")
    .abortSignal(signal);
  if (error) throw error;
  return data as Uom[];
}

export async function createItem(tenantId: string, input: ItemInput): Promise<string> {
  if (isDemoMode) return demoApi.createItem(input);
  const { data, error } = await db()
    .from("items")
    .insert({ tenant_id: tenantId, ...input })
    .select("id")
    .single();
  if (error) throw error;
  return (data as { id: string }).id;
}

export async function updateItem(id: string, input: Omit<ItemInput, "code">): Promise<void> {
  if (isDemoMode) return demoApi.updateItem(id, input);
  const { error } = await db().from("items").update(input).eq("id", id);
  if (error) throw error;
}

export async function addItemConversion(
  tenantId: string,
  itemId: string,
  fromUomId: string,
  toUomId: string,
  factor: string,
): Promise<void> {
  if (isDemoMode) return demoApi.addConversion(itemId, fromUomId, toUomId, factor);
  const { error } = await db()
    .from("uom_conversions")
    .insert({ tenant_id: tenantId, item_id: itemId, from_uom_id: fromUomId, to_uom_id: toUomId, factor });
  if (error) throw error;
}

export async function deleteItemConversion(id: string): Promise<void> {
  if (isDemoMode) return demoApi.deleteConversion(id);
  const { error } = await db().from("uom_conversions").delete().eq("id", id);
  if (error) throw error;
}

// ---------------------------------------------------------------------------
// Query options
// ---------------------------------------------------------------------------

export const itemKeys = {
  all: ["master-data", "items"] as const,
  list: (p: ItemListParams) => [...itemKeys.all, "list", p] as const,
  detail: (id: string) => [...itemKeys.all, "detail", id] as const,
};

export const uomKeys = { all: ["master-data", "uoms"] as const };

export function itemListQuery(p: ItemListParams) {
  return infiniteQueryOptions({
    queryKey: itemKeys.list(p),
    queryFn: ({ pageParam, signal }) => listItems(p, pageParam, signal),
    initialPageParam: null as string | null,
    getNextPageParam: (last) => last.nextCursor,
    staleTime: 60_000,
  });
}

export function itemDetailQuery(id: string) {
  return queryOptions({
    queryKey: itemKeys.detail(id),
    queryFn: ({ signal }) => getItem(id, signal),
    staleTime: 60_000,
  });
}

export const uomListQuery = queryOptions({
  queryKey: uomKeys.all,
  queryFn: ({ signal }) => listUoms(signal),
  // Units change rarely; one small request per session.
  staleTime: 10 * 60_000,
});
